import { withStyles } from '@material-ui/core/styles';
import React, { useCallback, useState } from 'react';
import { CREATE, Create, resolveRedirectTo, useNotify } from 'react-admin';
import { FormSpy } from 'react-final-form';
import CreateToolbar from '../../core/components/CreateToolbar';
import SimpleFormWithSave from '../../core/components/form/SimpleFormWithSave';
import omit from 'lodash/omit';
import FormTitleBar from '../../core/components/FormTitleBar';
import LeaveConfirm from '../../core/components/LeaveConfirmWrapper';
import NavigationPrompt from '../../core/components/NavigationPrompt';
import dataProvider from '../../core/providers/IccUserDataProvider';
import DocumentWatermarkSummary from './DocumentWatermarkSummary';
import {
  validateWatermarkName,
  getFormatFromValues
} from './DocumentWatermarkUtils';
import useNotifyServerError from '../../core/hooks/useNotifyServerError';

const styles = () => ({
  noActions: {
    marginTop: '0px'
  }
});

const DocumentWatermarkTitle = ({ record }) => {
  return (
    <FormTitleBar
      type="Watermark Profile"
      name=""
      status=""
      creation
      showAvatar
      avatarContent="WP"
      recordLabel="Watermark Profile"
    />
  );
};

const DocumentWatermarkCreate = props => {
  const [whenBlockNavigation, setWhenBlockNavigation] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const {
    classes,
    hasCreatePermission,
    hasEditPermission,
    hasDeletePermission,
    userProfile,
    ...rest
  } = props;
  const notify = useNotify();
  const notifyServerError = useNotifyServerError();

  const handleSubmit = useCallback(
    async values => {
      setSubmitting(true);

      let payload = omit(values, ['isBoldSelected', 'isItalicSelected']);

      // alignment and orientation cannot be used at the same time
      if (values.watermarkDisplay === 'direction') {
        payload.orientation = 'NOT_SET';
        // if REPEATING_DIAGONAL is selected, Stretched must be set to false
        if (values.alignment === 'REPEATING_DIAGONAL') {
          payload.stretched = false;
        }
      } else {
        payload.alignment = 'NOT_SET';
        payload.stretched = false; // default value in case Position is used
      }

      payload.format = getFormatFromValues(
        values.isBoldSelected,
        values.isItalicSelected
      );

      dataProvider(CREATE, 'documentwatermarks', { data: payload })
        .then(({ data }) => {
          notify('ra.notification.created');
          props.history.push(
            resolveRedirectTo('edit', props.basePath, data.id)
          );
        })
        .catch(notifyServerError)
        .finally(() => {
          setWhenBlockNavigation(false);
          setSubmitting(false);
        });
    },
    [notify, props.basePath, props.history, notifyServerError]
  );

  return (
    <React.Fragment>
      <NavigationPrompt when={whenBlockNavigation} creation>
        {(isOpen, onConfirm, onCancel) => (
          <LeaveConfirm
            isOpen={isOpen}
            onConfirm={onConfirm}
            onClose={onCancel}
          />
        )}
      </NavigationPrompt>
      <Create
        {...rest}
        title={<DocumentWatermarkTitle />}
        classes={{ noActions: classes.noActions }}
      >
        <SimpleFormWithSave
          toolbar={
            <CreateToolbar
              allowNavigationOnSave={() => setWhenBlockNavigation(false)}
            />
          }
          variant="standard"
          margin="normal"
          resource="documentwatermarks"
          validate={validateWatermarkName}
          onSave={handleSubmit}
          submitting={submitting}
          initialValues={{
            font: 'HELVETICA',
            fontSize: 8,
            alignment: 'UPPER_LEFT_TO_BOTTOM_RIGHT',
            orientation: 'TOP_LEFT',
            isBoldSelected: false,
            isItalicSelected: false,
            horizontalAlignment: 'LEFT_JUSTIFIED',
            fontColor: 'BLACK',
            watermarkDisplay: 'direction',
            stretched: false
          }}
        >
          <DocumentWatermarkSummary />
          <FormSpy
            subscription={{ dirty: true }}
            onChange={props => {
              setWhenBlockNavigation(props.dirty);
            }}
          />
        </SimpleFormWithSave>
      </Create>
    </React.Fragment>
  );
};

export default withStyles(styles)(DocumentWatermarkCreate);
